import * as Yup from "yup";
import { validation } from "@/constants/validation.translation";

/**
 * Returns true when the HTML string has no visible content
 * (e.g. "", "<p></p>", "<p>&nbsp;</p>", "<p><br></p>").
 * Images/embeds/rules count as content.
 * @param {string} html
 * @returns {boolean}
 */
export const isEmptyHtml = (html) => {
  if (!html || typeof html !== "string") return true;
  if (/<(img|iframe|video|audio|hr|table|oembed)\b/i.test(html)) return false;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/[​-‍﻿]/g, "")
    .trim();
  return text.length === 0;
};

/**
 * Validation class for the CMS form.
 * `translations` order is driven by the API (English first): English title + content
 * are required, other languages are optional, but if a title or content is entered
 * in an optional language, the other one becomes required so the row is complete.
 */
class CmsValidationClass {
  /**
   * @param {function} t - Translation function
   * @param {number|string} englishLanguageId - language_id of the English row
   * @returns {Yup.ObjectSchema}
   */
  static cmsValidation(t = (key) => key, englishLanguageId, requireSlug = false) {
    const { cms: cmsValidation } = validation;
    const isEnglish = (langId) => String(langId) === String(englishLanguageId);

    return Yup.object().shape({
      version: Yup.string()
        .trim()
        .required(t(cmsValidation.version.required))
        .matches(/^\d+\.\d+\.\d+$/, t(cmsValidation.version.pattern)),
      // Slug is only entered when adding a page; it is fixed afterwards
      ...(requireSlug && {
        slug: Yup.string()
          .trim()
          .required(t(cmsValidation.slug.required))
          .max(100, t(cmsValidation.slug.max_length))
          .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t(cmsValidation.slug.pattern)),
      }),
      translations: Yup.array().of(
        Yup.object().shape({
          language_id: Yup.number().required(),
          seo_meta_title: Yup.string().nullable().max(255, t(cmsValidation.seo_meta_title.max_length)),
          seo_meta_desc: Yup.string().nullable().max(255, t(cmsValidation.seo_meta_desc.max_length)),
          title: Yup.string()
            .trim()
            .max(255, t(cmsValidation.title.max_length))
            .test("title-required", t(cmsValidation.title.required), function (value) {
              const { language_id, description } = this.parent;
              if (isEnglish(language_id) || !isEmptyHtml(description)) {
                return !!value && value.trim().length > 0;
              }
              return true;
            }),
          description: Yup.string()
            .nullable()
            .test("content-required", t(cmsValidation.content.required), function (value) {
              const { language_id, title } = this.parent;
              if (isEnglish(language_id) || (title && title.trim().length > 0)) {
                return !isEmptyHtml(value);
              }
              return true;
            }),
        })
      ),
    });
  }
}

export default CmsValidationClass;
